/**
 * Seguridad · Usuarios — listado real desde el backend (admin-only).
 *
 * Pega a `GET /api/users` que solo responde si el JWT es de un admin
 * (middleware `requireAdmin`). El front, además, oculta el item del menú
 * lateral para no-admin (defensa en profundidad — ver Sidebar).
 *
 * Alta / edición / GLN default → próximos pasos.
 */

import { useState } from 'react';
import { Loader2, MapPin, Lock, Plus } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useUsuariosAdmin } from '@/api/queries';
import { cn } from '@/lib/utils';
import { fmtDateTime } from '@/lib/format';
import type { UserAdmin } from '@/api/contracts/users-admin';
import { NuevoUsuarioDialog } from './NuevoUsuarioDialog';

export function UsuariosPage() {
  const { data, isLoading, error } = useUsuariosAdmin();
  const [nuevoOpen, setNuevoOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-600">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        <span>Cargando usuarios...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          {error instanceof Error ? error.message : String(error)}
        </AlertDescription>
      </Alert>
    );
  }

  const users = data?.users ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-600">
          {users.length} usuario{users.length === 1 ? '' : 's'} registrado
          {users.length === 1 ? '' : 's'}.
        </p>
        <Button
          size="sm"
          onClick={() => setNuevoOpen(true)}
          className="bg-everex-naranja text-white hover:bg-everex-naranja-hover"
        >
          <Plus className="mr-1 h-4 w-4" />
          Nuevo
        </Button>
      </div>

      <NuevoUsuarioDialog open={nuevoOpen} onOpenChange={setNuevoOpen} />

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-[11px] uppercase text-gray-600">
                <tr>
                  <th className="px-3 py-2 text-left">Usuario</th>
                  <th className="px-3 py-2 text-left">Rol</th>
                  <th className="px-3 py-2 text-left">Email</th>
                  <th className="px-3 py-2 text-left">GLN(s) operables</th>
                  <th className="px-3 py-2 text-left">Último login</th>
                  <th className="px-3 py-2 text-center">Estado</th>
                </tr>
              </thead>
              <tbody className="text-gray-800">
                {users.map((u) => (
                  <UserRow key={u.id} user={u} />
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function UserRow({ user }: { user: UserAdmin }) {
  const locked = user.lockedUntil && new Date(user.lockedUntil) > new Date();
  return (
    <tr className={cn('border-t border-gray-100', !user.active && 'opacity-60')}>
      <td className="px-3 py-2">
        <div className="font-semibold text-everex-azul">
          {user.name ?? user.username}
        </div>
        <div className="font-mono text-[11px] text-gray-500">{user.username}</div>
      </td>
      <td className="px-3 py-2">
        <span
          className={cn(
            'rounded px-2 py-0.5 text-[10px] font-semibold uppercase',
            user.role.name === 'Administrador'
              ? 'bg-everex-azul/10 text-everex-azul'
              : 'bg-gray-100 text-gray-700',
          )}
        >
          {user.role.name}
        </span>
      </td>
      <td className="px-3 py-2 font-mono text-[11px]">{user.email ?? '—'}</td>
      <td className="px-3 py-2">
        {user.glns.length === 0 ? (
          <span className="text-[11px] italic text-gray-400">sin GLNs</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {user.glns.map((g) => (
              <span
                key={g.id}
                className={cn(
                  'inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-mono text-[10px]',
                  g.isDefault
                    ? 'bg-everex-naranja/10 text-everex-naranja'
                    : 'bg-gray-100 text-gray-700',
                )}
                title={`${g.name ?? ''}${g.isDefault ? ' (default)' : ''}`}
              >
                {g.isDefault && <MapPin className="h-2.5 w-2.5" />}
                {g.gln}
              </span>
            ))}
          </div>
        )}
      </td>
      <td className="px-3 py-2 text-[11px] text-gray-600">
        {user.lastLoginAt ? fmtDateTime(user.lastLoginAt) : '—'}
      </td>
      <td className="px-3 py-2 text-center">
        {!user.active ? (
          <span className="rounded bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600">
            Inactivo
          </span>
        ) : locked ? (
          <span
            className="inline-flex items-center gap-1 rounded bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700"
            title={`Bloqueado hasta ${fmtDateTime(user.lockedUntil)}`}
          >
            <Lock className="h-2.5 w-2.5" />
            Bloqueado
          </span>
        ) : (
          <span className="rounded bg-green-100 px-2 py-0.5 text-[10px] font-semibold text-green-700">
            Activo
          </span>
        )}
      </td>
    </tr>
  );
}
